import { useEffect, useRef, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { getHasCachedArtifactProcessingOperation } from "@virtual-brain-lib/frontend-api";
import type {
	AnswerError,
	AnsweringFinishedEvent,
	AnsweringMessageUpdateEvent,
	AnsweringSourcesUpdateEvent,
	AnsweringStartedEvent,
	AnsweringToolUpdateEvent,
	Chat,
	ChatMessageWithUIState,
	Events,
	MessageTool,
} from "@virtual-brain-lib/types";

import type { OpenArtifactParams } from "../artifact-panel/useArtifactPanel";
import { useChatContext, useChatStore, useOpenedArtifactStore } from "../core";

export default function useStreamedMessage({
	message,
	chatId,
	openArtifact,
	isLastMessage,
	updateCachedMessage,
}: {
	message: ChatMessageWithUIState;
	chatId: Chat["id"];
	openArtifact?: (params: OpenArtifactParams) => void;
	isLastMessage: boolean;
	updateCachedMessage?: (messageId: string, updates: Partial<ChatMessageWithUIState>) => void;
}): ChatMessageWithUIState {
	const queryClient = useQueryClient();
	const autoOpenedArtifactIdsRef = useRef<Set<string>>(new Set());
	const setAnsweringMessage = useChatStore((store) => store.setAnsweringMessage);
	const openedArtifact = useOpenedArtifactStore((state) => state.openedArtifact);

	const { socket, user } = useChatContext();
	const [streamedMessageUpdates, setStreamedMessageUpdates] = useState<
		Partial<ChatMessageWithUIState>
	>({});

	useEffect(() => {
		if (message.role !== "assistant" || message.status !== "processing") return;

		setAnsweringMessage({
			messageId: message.id,
			isAnswering: true,
		});
	}, [message.id, message.role, message.status, message.answeringStep]);

	useEffect(() => {
		if (!socket || message.role !== "assistant") return;

		function handleAnswerStarted(event: AnsweringStartedEvent) {
			if (event.chatId !== chatId || event.message.id !== message.id) {
				return;
			}

			setAnsweringMessage({
				messageId: message.id,
				isAnswering: true,
			});

			const startedUpdates: Partial<ChatMessageWithUIState> = {
				...event.message,
				contents: event.contents,
				isThinking: true,
				isAnswering: false,
				status: "processing",
				answeringStep: { step: "analyzing", status: "pending" },
			};

			setStreamedMessageUpdates((previous) => ({
				...previous,
				isThinking: true,
				isAnswering: false,
				status: "processing",
				answeringStep: { step: "analyzing", status: "pending" },
			}));

			updateCachedMessage?.(message.id, startedUpdates);
		}

		function handleSourcesUpdate(event: AnsweringSourcesUpdateEvent) {
			if (event.chatId !== chatId || event.messageId !== message.id) {
				return;
			}

			setStreamedMessageUpdates((previous) => ({
				...previous,
				sources: event.sources,
				contents: event.contents,
			}));
		}

		function autoOpenArtifact(artifactId: string, artifactName: string) {
			if (!isLastMessage) {
				return;
			}

			if (autoOpenedArtifactIdsRef.current.has(artifactId)) {
				return;
			}

			autoOpenedArtifactIdsRef.current.add(artifactId);
			openArtifact?.({ artifactId, artifactName, assistantMessageId: message.id });
		}

		function handleMessageUpdate(event: AnsweringMessageUpdateEvent) {
			if (event.chatId !== chatId || event.messageId !== message.id) {
				return;
			}

			if (event?.loadingArtifact?.id) {
				autoOpenArtifact(event.loadingArtifact.id, event.loadingArtifact.name ?? "");
			}

			setStreamedMessageUpdates((previous) => ({
				...previous,
				thinkingContent: event.thinkingContent,
				content: event.content,
				loadingArtifact: event.loadingArtifact,
				artifacts: event.artifacts,
				isThinking: false,
				isAnswering: true,
			}));
		}

		function handleAnswerFinished(event: AnsweringFinishedEvent) {
			if (event.chatId !== chatId || event.message.id !== message.id) {
				return;
			}

			setAnsweringMessage({
				messageId: message.id,
				isAnswering: false,
			});

			const finalCachedUpdates = {
				...event.message,
				contents: event.contents ?? message.contents,
				loadingArtifact: undefined,
				isThinking: false,
				isAnswering: false,
			};

			setStreamedMessageUpdates((previous) => ({
				...previous,
				...finalCachedUpdates,
			}));

			updateCachedMessage?.(message.id, finalCachedUpdates);

			const firstArtifact = event.message.artifacts?.[0];

			if (firstArtifact?.id) {
				autoOpenArtifact(firstArtifact.id, firstArtifact.name ?? "");
			}
		}

		function handleAnswerError(event: AnswerError) {
			if (event.chatId !== chatId || event.messageId !== message.id) {
				return;
			}

			setAnsweringMessage({
				messageId: message.id,
				isAnswering: false,
			});
		}

		function handleToolUpdate(event: AnsweringToolUpdateEvent) {
			const { chatId: eventChatId, messageId: eventMessageId, ...eventMessageTool } = event;

			if (eventChatId !== chatId || eventMessageId !== message.id) {
				return;
			}

			function upsertTool(tools: MessageTool[] = []): MessageTool[] {
				return tools.some((tool) => tool.type === event.type)
					? tools.map((tool) =>
							tool.type === event.type ? mergeTool(tool, eventMessageTool) : tool,
						)
					: [...tools, eventMessageTool];
			}

			const tools = upsertTool(message.tools);

			setStreamedMessageUpdates((previous) => ({
				...previous,
				tools,
			}));

			updateCachedMessage?.(message.id, { tools });
		}

		const abortStream = (event: Event) => {
			const { chatId: eventChatId } = (event as CustomEvent<{ chatId: Chat["id"] }>).detail;

			if (!socket || eventChatId !== chatId) return;

			const isArtifactProcessing = (message.artifacts ?? []).some((artifact) =>
				getHasCachedArtifactProcessingOperation({
					queryClient,
					artifactId: artifact.id,
				}),
			);

			if (message.status !== "processing" && !isArtifactProcessing) return;

			socket.emit("streamResponse:abort", {
				userId: user?.id,
				chatId,
				messageId: message.id,
			});
		};

		window.addEventListener("abortChatStream", abortStream);
		socket.on<Events>("answering:started", handleAnswerStarted);
		socket.on<Events>("answering:sources-update", handleSourcesUpdate);
		socket.on<Events>("answering:message-update", handleMessageUpdate);
		socket.on<Events>("answering:tool-update", handleToolUpdate);
		socket.on<Events>("answering:finished", handleAnswerFinished);
		socket.on<Events>("answering:error", handleAnswerError);

		return () => {
			socket.off<Events>("answering:started", handleAnswerStarted);
			socket.off("answering:sources-update", handleSourcesUpdate);
			socket.off("answering:message-update", handleMessageUpdate);
			socket.off("answering:tool-update", handleToolUpdate);
			socket.off("answering:finished", handleAnswerFinished);
			socket.off("answering:error", handleAnswerError);
			window.removeEventListener("abortChatStream", abortStream);
		};
	}, [
		message.id,
		// `abortStream` reads the status to know whether there is something to stop,
		// so the listener must be re-registered whenever it changes.
		message.status,
		message.tools,
		chatId,
		socket,
		openedArtifact?.artifactId,
		openedArtifact?.assistantMessageId,
	]);

	useEffect(
		() => () => {
			const { answeringByMessageId, setAnsweringMessage } = useChatStore.getState();

			if (answeringByMessageId.has(message.id)) {
				setAnsweringMessage({
					messageId: message.id,
					isAnswering: false,
				});
			}
		},
		[message.id],
	);

	return { ...message, ...streamedMessageUpdates };
}

function mergeTool(existingTool: MessageTool, incomingTool: MessageTool): MessageTool {
	if (
		existingTool.type !== "codeBasedTableAnalysis" ||
		incomingTool.type !== "codeBasedTableAnalysis"
	) {
		return incomingTool;
	}

	// Parallel analysis jobs each emit only their own document, and the API card lacks live steps
	const existingDocuments = existingTool.data.documents.map((existingDocument) => {
		const incomingDocument = incomingTool.data.documents.find(
			({ id }) => id === existingDocument.id,
		);

		if (!incomingDocument) {
			return existingDocument;
		}

		return incomingDocument.steps.length
			? incomingDocument
			: { ...incomingDocument, steps: existingDocument.steps };
	});
	const newDocuments = incomingTool.data.documents.filter(
		({ id }) => !existingTool.data.documents.some((document) => document.id === id),
	);

	return {
		...incomingTool,
		data: { ...incomingTool.data, documents: [...existingDocuments, ...newDocuments] },
	};
}
